import { parseMoneyToCents, startOfDay, toDateParam, type MoneyInput } from '@agos/shared'

/**
 * External drivers — couriers who are not employees and are paid per delivery.
 *
 * §9.2 said this feature was broken, and the diagnosis is in
 * `docs/pages/manager-delivery/external-driver.md`: **nothing could create an
 * external driver.** `is_external` had no write path in either backend, so this
 * screen managed a set that was always empty.
 *
 * **It has one now — corrected 2026-08-05, this still said otherwise in the
 * present tense.** `EmployeeService` names `is_external` in its `INSERT` citing
 * §9.2, assigns it on update when the body carries `isExternal`, and the
 * employee dialog posts it from a checkbox. The paragraph above promised this
 * page would work on the first driver the backend produced; §9.2's journey e2e
 * seeds one and walks it, so that promise is kept rather than pending.
 *
 * The set is still empty on the restored dump — no franchise has created one —
 * but that is now a fact about the data rather than about the capability, and
 * the two read identically on screen. That is the distinction worth keeping.
 */

export interface ExternalDriver {
  id?: number
  name?: string
  position?: string
  phone?: string | null
  email?: string | null
  active?: boolean
  isAvailable?: boolean
  emailVerified?: boolean
  adminApproved?: boolean
  ratePerOrder?: MoneyInput
  ratePerKm?: MoneyInput
  deliveryCount?: number
  totalKm?: number
  reward?: MoneyInput
}

export type Period = 'today' | 'week' | 'month' | 'custom'

/**
 * The period buttons, computed from local components.
 *
 * `toDateParam` rather than `toISOString().slice(0, 10)` — FB-034 for the fourth
 * time. Legacy's `weekStart()` also mutates the `Date` it is measuring from
 * (`new Date(d.setDate(diff))` sets `d` as a side effect), which is harmless
 * only because it is discarded immediately after.
 */
export function periodRange(
  period: Period,
  custom: { from: string; to: string },
  now: Date,
): { from: string; to: string } {
  const today = toDateParam(now)

  if (period === 'today') return { from: today, to: today }

  if (period === 'week') {
    const monday = startOfDay(now)
    const weekday = monday.getDay()
    // Sunday is day 0 and belongs to the week that started six days earlier.
    monday.setDate(monday.getDate() - (weekday === 0 ? 6 : weekday - 1))
    return { from: toDateParam(monday), to: today }
  }

  if (period === 'month') {
    const first = startOfDay(now)
    first.setDate(1)
    return { from: toDateParam(first), to: today }
  }

  return custom
}

/**
 * Where a driver is in the two-gate sign-up.
 *
 * Both gates must pass before they can log in: they confirm their own email, and
 * then an administrator approves them. `blocked` is the state the page exists to
 * clear.
 */
export type DriverGate = 'ready' | 'awaiting-approval' | 'awaiting-email' | 'blocked'

export function driverGate(driver: ExternalDriver): DriverGate {
  const verified = driver.emailVerified === true
  const approved = driver.adminApproved === true
  if (verified && approved) return 'ready'
  if (verified) return 'awaiting-approval'
  if (approved) return 'awaiting-email'
  return 'blocked'
}

/** Whether an administrator pressing *approve* would actually let them in. */
export function approvalUnblocks(driver: ExternalDriver): boolean {
  return driverGate(driver) === 'awaiting-approval'
}

export interface ExternalTotals {
  driverCount: number
  deliveries: number
  kilometres: number
  /** In cents — §14.7. This is a payout. */
  rewardCents: number
}

/**
 * What the franchise owes.
 *
 * `reward` is computed server-side as `deliveries × ratePerOrder + km × ratePerKm`
 * and is trusted, because the rate that applied at the time may not be the rate
 * on the record now. Summed in cents: this is money leaving the business.
 */
export function externalTotals(drivers: ExternalDriver[]): ExternalTotals {
  return {
    driverCount: drivers.length,
    deliveries: drivers.reduce((total, driver) => total + (driver.deliveryCount ?? 0), 0),
    kilometres: drivers.reduce((total, driver) => total + (driver.totalKm ?? 0), 0),
    rewardCents: drivers.reduce((total, driver) => total + parseMoneyToCents(driver.reward), 0),
  }
}

/**
 * What the server would compute for these rates, so an administrator editing them
 * can see the consequence before saving.
 *
 * Deliberately the same shape as the backend's expression rather than a
 * simplification — if the two ever disagree, the number a manager approved is not
 * the number that gets paid.
 */
export function projectedRewardCents(
  driver: ExternalDriver,
  rates: { ratePerOrder: MoneyInput; ratePerKm: MoneyInput },
): number {
  const perOrder = parseMoneyToCents(rates.ratePerOrder) * (driver.deliveryCount ?? 0)
  // Kilometres are fractional, so this is the one multiplication that has to
  // round — and it rounds once, at the end, like every other rate in the app
  // (FB-056).
  const perKm = Number(rates.ratePerKm ?? 0) * (driver.totalKm ?? 0) * 100
  const rounded = perKm < 0 ? -Math.round(-perKm) : Math.round(perKm)
  return perOrder + rounded
}

/** A rate a manager typed. Negative is a typo; empty is zero. */
export function isValidRate(value: string): boolean {
  if (value.trim() === '') return true
  const parsed = Number(value.replace(',', '.'))
  return Number.isFinite(parsed) && parsed >= 0
}
